/* Mission Control: the Partners constellation.
   Nodes and the core are placed by JS on whole pixels (never scaled), so every border, logo, and label stays
   perfectly sharp in every camera position. The "camera" is a spread and pan of positions, eased per frame;
   the links and signal pulses are redrawn from the same positions. On desktop the constellation is sticky
   beside the mission log: as each entry comes into focus, its node lights, the camera eases toward it, and
   pulses stream down its link. Paused off-screen; reduced motion places everything once, with no camera.
   Without JS, CSS positions the nodes by percentage. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const design = document.documentElement.classList.contains('shopify-design-mode');
  const wide = window.matchMedia('(min-width: 990px)');
  const tablet = window.matchMedia('(min-width: 750px)');

  function Constellation(root) {
    const section = root.closest('.mc-network') || document;
    const cam = root.querySelector('[data-mc-cam]') || root;
    const svg = root.querySelector('[data-mc-links]');
    const canvas = root.querySelector('[data-mc-pulses]');
    const core = root.querySelector('[data-mc-core]');
    const nodes = [...root.querySelectorAll('[data-mc-node]')];
    const entries = [...section.querySelectorAll('[data-mc-entry]')];
    const log = section.querySelector('.mc-plog');
    if (!svg || !canvas || !core || !nodes.length) return;
    const ctx = canvas.getContext('2d');
    let W = 0, H = 0, dpr = 1, base = [], baseCore = { x: 0, y: 0 }, curves = [], paths = [];
    let pulses = [], raf = 0, last = 0, visible = false, spawnT = 0, focus = -1;
    const camNow = { x: 0, y: 0, s: 1 }, camTo = { x: 0, y: 0, s: 1 };
    let drawn = false;

    const pct = (el, prop) => { const v = parseFloat(getComputedStyle(el).getPropertyValue(prop)); return Number.isNaN(v) ? 0.5 : v / 100; };
    function measure() {
      W = cam.offsetWidth; H = cam.offsetHeight;
      if (!W || !H) return false;
      dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
      const px = tablet.matches ? '--x' : '--mx', py = tablet.matches ? '--y' : '--my';
      base = nodes.map((n) => ({ x: pct(n, px) * W, y: pct(n, py) * H, w: n.offsetWidth, h: n.offsetHeight }));
      baseCore = { x: W / 2, y: H / 2, w: core.offsetWidth, h: core.offsetHeight };
      return true;
    }
    const project = (p, c) => ({ x: (p.x - W / 2) * c.s + W / 2 + c.x, y: (p.y - H / 2) * c.s + H / 2 + c.y });

    // Place every element on whole pixels and redraw links from the same positions
    function render() {
      // Cancel the frame's own sub-pixel offset (centred columns, sticky offsets) so nodes land on whole screen pixels
      const fr = cam.getBoundingClientRect();
      const fx = fr.left - Math.round(fr.left), fy = fr.top - Math.round(fr.top);
      const cpos = project(baseCore, camNow);
      core.style.setProperty('--tx', `${Math.round(cpos.x - baseCore.w / 2) - fx}px`);
      core.style.setProperty('--ty', `${Math.round(cpos.y - baseCore.h / 2) - fy}px`);
      const c = { x: Math.round(cpos.x), y: Math.round(cpos.y) };
      curves = base.map((b, i) => {
        const q0 = project(b, camNow);
        nodes[i].style.setProperty('--tx', `${Math.round(q0.x - b.w / 2) - fx}px`);
        nodes[i].style.setProperty('--ty', `${Math.round(q0.y - b.h / 2) - fy}px`);
        const p = { x: Math.round(q0.x), y: Math.round(q0.y) };
        const mx = (c.x + p.x) / 2, my = (c.y + p.y) / 2;
        const dx = p.x - c.x, dy = p.y - c.y, len = Math.hypot(dx, dy) || 1;
        const bow = (i % 2 ? 1 : -1) * Math.min(40, len * 0.12);
        return { a: c, b: p, q: { x: mx - (dy / len) * bow, y: my + (dx / len) * bow } };
      });
      const d = curves.map((k) => `M${k.a.x} ${k.a.y} Q${k.q.x.toFixed(1)} ${k.q.y.toFixed(1)} ${k.b.x} ${k.b.y}`);
      const ringPts = curves.map((k) => k.b).sort((u, v) => Math.atan2(u.y - c.y, u.x - c.x) - Math.atan2(v.y - c.y, v.x - c.x));
      const ring = ringPts.length > 2 ? `M${ringPts.map((p) => `${p.x} ${p.y}`).join(' L')} Z` : '';
      if (!paths.length) {
        svg.innerHTML = d.map((v) => `<path d="${v}"/>`).join('') + (ring ? `<path class="is-dashed" d="${ring}"/>` : '');
        paths = [...svg.querySelectorAll('path:not(.is-dashed)')];
        if (!reduced && !design && !drawn) {
          paths.forEach((path, i) => {
            const L = path.getTotalLength();
            path.style.strokeDasharray = `${L}`;
            path.style.strokeDashoffset = `${L}`;
            path.style.transition = `stroke-dashoffset 1400ms cubic-bezier(0.16, 1, 0.3, 1) ${200 + i * 120}ms, stroke 600ms, stroke-width 600ms`;
          });
        }
      } else {
        paths.forEach((path, i) => path.setAttribute('d', d[i]));
        const dash = svg.querySelector('.is-dashed');
        if (dash && ring) dash.setAttribute('d', ring);
      }
      paths.forEach((pth, k) => pth.classList.toggle('is-focus', k === focus));
    }
    function build() { if (measure()) { applyCamera(true); render(); } }
    const at = (k, t) => {
      const u = 1 - t;
      return { x: u * u * k.a.x + 2 * u * t * k.q.x + t * t * k.b.x, y: u * u * k.a.y + 2 * u * t * k.q.y + t * t * k.b.y };
    };

    // Camera target: ease toward the focused node, but never push any node (or the core) out of the frame
    function applyCamera(snap) {
      camTo.x = 0; camTo.y = 0; camTo.s = 1;
      if (focus >= 0 && base[focus] && wide.matches && !reduced && !design) {
        const all = [...base, baseCore];
        const fit = (s) => {
          let lo = -Infinity, hi = Infinity, vlo = -Infinity, vhi = Infinity;
          all.forEach((b) => {
            const x = (b.x - W / 2) * s + W / 2, y = (b.y - H / 2) * s + H / 2;
            lo = Math.max(lo, b.w / 2 + 10 - x); hi = Math.min(hi, W - b.w / 2 - 10 - x);
            vlo = Math.max(vlo, b.h / 2 + 10 - y); vhi = Math.min(vhi, H - b.h / 2 - 10 - y);
          });
          return lo <= hi && vlo <= vhi ? { lo, hi, vlo, vhi } : null;
        };
        let s = 1.12, r = fit(s);
        while (!r && s > 1.001) { s = Math.max(1, s - 0.02); r = fit(s); }
        if (r) {
          const p = base[focus];
          const clampv = (v, a, b) => Math.min(b, Math.max(a, v));
          camTo.s = s;
          camTo.x = clampv((W / 2 - ((p.x - W / 2) * s + W / 2)) * 0.35, r.lo, r.hi);
          camTo.y = clampv((H / 2 - ((p.y - H / 2) * s + H / 2)) * 0.35, r.vlo, r.vhi);
        }
      }
      if (snap) { camNow.x = camTo.x; camNow.y = camTo.y; camNow.s = camTo.s; }
    }

    function applyFocus(i, quiet) {
      focus = i;
      root.classList.toggle('has-focus', i >= 0);
      nodes.forEach((n, k) => n.classList.toggle('is-focus', k === i));
      applyCamera(false);
      if (reduced || design) { camNow.x = camTo.x; camNow.y = camTo.y; camNow.s = camTo.s; render(); return; }
      if (!quiet) burst();
      kick();
    }
    function burst() {
      if (focus < 0 || !visible || pulses.length > 12 || !curves[focus]) return;
      for (let j = 0; j < 3; j++) pulses.push({ i: focus, t: -j * 0.12, v: 0.7, back: false, hot: true });
    }

    function frame(now) {
      raf = 0;
      if (!visible || document.hidden) return;
      const dt = Math.min(0.05, (now - (last || now)) / 1000);
      last = now;
      // Ease the camera (critically damped, ~1.2s), then render on whole pixels
      const k = 1 - Math.pow(0.02, dt / 1.2);
      const moving = Math.abs(camTo.x - camNow.x) > 0.3 || Math.abs(camTo.y - camNow.y) > 0.3 || Math.abs(camTo.s - camNow.s) > 0.0005;
      if (moving) {
        camNow.x += (camTo.x - camNow.x) * k; camNow.y += (camTo.y - camNow.y) * k; camNow.s += (camTo.s - camNow.s) * k;
        render();
      } else if (camNow.x !== camTo.x || camNow.y !== camTo.y || camNow.s !== camTo.s) {
        camNow.x = camTo.x; camNow.y = camTo.y; camNow.s = camTo.s;
        render();
      }
      spawnT -= dt;
      if (spawnT <= 0 && pulses.length < 10) {
        const i = focus >= 0 && Math.random() < 0.6 ? focus : (Math.random() * curves.length) | 0;
        pulses.push({ i, t: 0, v: 0.32 + Math.random() * 0.25, back: Math.random() < 0.3, hot: i === focus });
        spawnT = 0.35 + Math.random() * 0.5;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      pulses = pulses.filter((p) => (p.t += p.v * dt) < 1);
      pulses.forEach((p) => {
        const kv = curves[p.i];
        if (p.t < 0 || !kv) return;
        for (let j = 0; j < 5; j++) {
          const t = p.back ? 1 - Math.max(0, p.t - j * 0.035) : Math.max(0, p.t - j * 0.035);
          const pt = at(kv, t);
          const s = j === 0 ? (p.hot ? 4 : 3) : 2;
          ctx.fillStyle = j === 0 ? 'rgba(233, 246, 255, 0.95)' : `rgba(124, 200, 255, ${(0.55 - j * 0.1).toFixed(2)})`;
          ctx.fillRect(Math.round(pt.x - s / 2), Math.round(pt.y - s / 2), s, s);
        }
      });
      raf = requestAnimationFrame(frame);
    }
    function kick() { if (!raf && visible && !reduced && !design) { last = 0; raf = requestAnimationFrame(frame); } }

    root.classList.add('is-js');
    if (!reduced && !design) root.classList.add('is-armed');
    build();
    new ResizeObserver(() => build()).observe(cam);
    // Phones and tablets scroll the frame with the page; re-snap once scrolling settles
    let snapT = 0;
    window.addEventListener('scroll', () => { clearTimeout(snapT); snapT = setTimeout(() => { if (visible) render(); }, 120); }, { passive: true });
    document.fonts?.ready.then(build);
    nodes.forEach((n) => n.querySelector('img')?.addEventListener('load', build));
    tablet.addEventListener?.('change', build);
    new IntersectionObserver((e) => {
      visible = e[0].isIntersecting;
      if (visible && !root.classList.contains('is-live')) {
        root.classList.add('is-live');
        drawn = true;
        paths.forEach((p) => { p.style.strokeDashoffset = '0'; });
        // Once drawn in, links drop their dash pattern so the camera can reshape them freely
        setTimeout(() => paths.forEach((p) => { p.style.strokeDasharray = ''; p.style.strokeDashoffset = ''; }), 2400);
      }
      kick();
    }, { threshold: 0.15 }).observe(root);
    document.addEventListener('visibilitychange', kick);

    // The story drives the focus: the entry crossing the middle of the viewport owns the constellation
    if (!reduced && !design && entries.length && 'IntersectionObserver' in window) {
      const nameIndex = (name) => nodes.findIndex((n) => (n.dataset.name || '').trim().toLowerCase() === (name || '').trim().toLowerCase());
      const active = new Set();
      let lastEntry = null;
      const io = new IntersectionObserver((list) => {
        list.forEach((en) => { if (en.isIntersecting) active.add(en.target); else active.delete(en.target); });
        const current = entries.find((e) => active.has(e));
        const changed = current !== lastEntry;
        lastEntry = current;
        entries.forEach((e) => e.classList.toggle('is-focus', e === current));
        log?.classList.toggle('has-focus', !!current);
        const idx = current ? nameIndex(current.dataset.mcEntry) : -1;
        if (idx !== focus) applyFocus(idx);
        else if (changed && current) burst();
      }, { rootMargin: '-45% 0px -45% 0px' });
      entries.forEach((e) => io.observe(e));
      wide.addEventListener?.('change', () => { applyCamera(true); render(); });
    }
  }

  function init() { document.querySelectorAll('[data-mc-constellation]').forEach((el) => { if (!el.__mc) { el.__mc = 1; Constellation(el); } }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
